/**
 * Workspace Auth Provider implementations for JS SDK.
 *
 * SessionTokenProvider: Full challenge-signing flow (Spec §6.1, §9.1).
 *   - Fetches challenges from POST /auth/challenge
 *   - Signs auth-proof payload with Ed25519 (readerSeed or writerSeed)
 *   - Creates sessions via POST /auth/session
 *   - Caches tokens with proactive refresh (30s margin)
 *   - Invalidation on 401 for re-auth
 *
 * StaticTokenProvider: Fixed token for tests.
 */

import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'
import type { WorkspaceInviteV2, WorkspaceSecretV2 } from './workspaceSecretV2'
import type { WorkspaceAccessToken, WorkspaceAuthProvider } from './workspaceSecretV2'
import {
    base64UrlEncode,
    base64UrlDecode,
    canonicalJsonStringify,
    decodeWorkspaceSecretV2,
    deriveKid,
    encodeWorkspaceSecretV2,
    encodeWorkspaceInviteV2,
} from './workspaceSecretV2'
import { recordBrowserHttpErrorResponse } from './browserHttpErrors'

// Ensure sha512 is configured for noble-ed25519 v2.x (idempotent)
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m))
ed.etc.sha512Async = (...m) => Promise.resolve(sha512(ed.etc.concatBytes(...m)))

/** Proactive refresh margin: 30 seconds before expiry */
const REFRESH_MARGIN_MS = 30_000
const DEFAULT_AUTH_REQUEST_TIMEOUT_MS = 10_000
const AUTH_CHALLENGE_MAX_ATTEMPTS = 4
const AUTH_CHALLENGE_RETRY_BASE_MS = 250
const providerCache = new Map<string, SessionTokenProvider>()

function getAuthRequestTimeoutMs(): number {
    const raw = typeof process !== 'undefined' ? process.env?.SECUREBLOB_AUTH_REQUEST_TIMEOUT_MS : undefined
    if (!raw) return DEFAULT_AUTH_REQUEST_TIMEOUT_MS
    const parsed = Number.parseInt(raw, 10)
    if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_AUTH_REQUEST_TIMEOUT_MS
    return parsed
}

/**
 * Ephemeral storage header for E2E tests (mirrors storeBackends.ts).
 */
function getEphemeralHeaders(): Record<string, string> {
    const secret = typeof window !== 'undefined' && (window as any).__e2eStorageSecret
    if (secret) {
        return { 'X-Storage-Mode': `memory:${secret}` }
    }
    return {}
}

async function createSubjectProof(
    seed: Uint8Array,
    blobId: string,
    challengeId: string,
    scope: 'read' | 'write',
    subjectKind: 'reader' | 'writer',
    subjectKidOverride?: string,
): Promise<{ alg: 'ed25519'; kid: string; pk: string; sig: string }> {
    const pk = await ed.getPublicKey(seed)
    const kid = await deriveKid(pk)
    const subjectKid = subjectKidOverride ?? kid
    const payload = {
        v: 1,
        type: 'workspace-auth-proof',
        challengeId,
        blobId,
        scope,
        subjectKind,
        subjectKid,
    }
    const canonicalBytes = new TextEncoder().encode(canonicalJsonStringify(payload))
    const hashBuf = await crypto.subtle.digest('SHA-256', canonicalBytes)
    const signature = await ed.sign(new Uint8Array(hashBuf), seed)

    return {
        alg: 'ed25519',
        kid,
        pk: base64UrlEncode(pk),
        sig: base64UrlEncode(signature),
    }
}

async function fetchChallenge(
    apiBaseUrl: string,
    blobId: string,
    scope: 'read' | 'write',
    subjectKind: 'reader' | 'writer',
    signal?: AbortSignal,
): Promise<{ challengeId: string; expiresAt: string }> {
    const challengeUrl = `${apiBaseUrl}/auth/challenge`
    const res = await fetchAuthResponseWithRetry(challengeUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...getEphemeralHeaders() },
        body: JSON.stringify({ blobId, scope, subjectKind }),
        signal,
    }, 'POST /auth/challenge', signal)
    if (!res.ok) throw new Error(`Auth challenge failed: ${res.status}`)
    return (await res.json()) as { challengeId: string; expiresAt: string }
}

function getRetryAfterMs(headerValue: string | null): number | null {
    if (!headerValue) return null

    const seconds = Number(headerValue)
    if (Number.isFinite(seconds)) {
        return Math.max(0, seconds * 1000)
    }

    const retryAt = Date.parse(headerValue)
    if (Number.isNaN(retryAt)) return null
    return Math.max(0, retryAt - Date.now())
}

function delayWithSignal(ms: number, signal?: AbortSignal): Promise<void> {
    if (ms <= 0) return Promise.resolve()
    return new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
            if (signal) signal.removeEventListener('abort', onAbort)
            resolve()
        }, ms)

        const onAbort = () => {
            clearTimeout(timer)
            if (signal) signal.removeEventListener('abort', onAbort)
            reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
        }

        if (signal?.aborted) {
            onAbort()
            return
        }

        signal?.addEventListener('abort', onAbort, { once: true })
    })
}

async function fetchAuthResponseWithRetry(
    input: string,
    init: RequestInit,
    label: string,
    signal?: AbortSignal,
): Promise<Response> {
    const timeoutMs = getAuthRequestTimeoutMs()
    for (let attempt = 1; attempt <= AUTH_CHALLENGE_MAX_ATTEMPTS; attempt++) {
        const res = await fetchWithTimeout(input, init, timeoutMs, label)
        if (res.status !== 429 || attempt >= AUTH_CHALLENGE_MAX_ATTEMPTS) {
            recordBrowserHttpErrorResponse(res, {
                url: input,
                method: init.method,
            })
            return res
        }

        const retryAfterMs = getRetryAfterMs(res.headers.get('retry-after'))
            ?? AUTH_CHALLENGE_RETRY_BASE_MS * (2 ** (attempt - 1))
        await delayWithSignal(retryAfterMs, signal)
    }

    throw new Error(`${label} failed: retries exhausted`)
}

function createAbortSignalWithTimeout(signal: AbortSignal | null | undefined, timeoutMs: number): {
    readonly signal: AbortSignal
    readonly didTimeout: () => boolean
    readonly cleanup: () => void
} {
    const controller = new AbortController()
    let didTimeout = false
    const onAbort = () => {
        controller.abort(signal?.reason)
    }

    if (signal?.aborted) {
        controller.abort(signal.reason)
    } else if (signal) {
        signal.addEventListener('abort', onAbort, { once: true })
    }

    const timeout = setTimeout(() => {
        didTimeout = true
        controller.abort()
    }, timeoutMs)

    return {
        signal: controller.signal,
        didTimeout: () => didTimeout,
        cleanup: () => {
            clearTimeout(timeout)
            if (signal) {
                signal.removeEventListener('abort', onAbort)
            }
        },
    }
}

async function fetchWithTimeout(
    input: string,
    init: RequestInit,
    timeoutMs: number,
    label: string,
): Promise<Response> {
    const { signal, didTimeout, cleanup } = createAbortSignalWithTimeout(init.signal ?? undefined, timeoutMs)
    try {
        const fetchPromise = fetch(input, { ...init, signal })
        const timeoutPromise = new Promise<Response>((_, reject) => {
            signal.addEventListener('abort', () => {
                if (didTimeout()) {
                    reject(new Error(`Auth request timed out after ${timeoutMs}ms: ${label}`))
                }
            }, { once: true })
        })
        return await Promise.race([fetchPromise, timeoutPromise])
    } catch (error) {
        if (didTimeout()) {
            throw new Error(`Auth request timed out after ${timeoutMs}ms: ${label}`)
        }
        throw error
    } finally {
        cleanup()
    }
}

export interface RedeemWorkspaceInviteResult {
    blobId: string
    readToken?: string
    writeToken?: string
    expiresAt?: string
    alreadyRedeemed: boolean
    secret: WorkspaceSecretV2
}

export function createWorkspaceAuthProvider(
    apiBaseUrl: string,
    encodedSecret: string,
    expectedBlobId?: string,
): SessionTokenProvider | null {
    const secret = decodeWorkspaceSecretV2(encodedSecret)
    if (!secret) return null

    if (expectedBlobId && secret.blobId !== expectedBlobId) {
        throw new Error(`Workspace secret blobId '${secret.blobId}' does not match entry blobId '${expectedBlobId}'.`)
    }

    const cacheKey = `${apiBaseUrl}\n${encodedSecret}`
    let provider = providerCache.get(cacheKey)
    if (!provider) {
        provider = new SessionTokenProvider(secret.blobId, apiBaseUrl, secret)
        providerCache.set(cacheKey, provider)
    }
    return provider
}

export async function redeemWorkspaceInviteV2(
    invite: WorkspaceInviteV2,
    signal?: AbortSignal,
): Promise<RedeemWorkspaceInviteResult> {
    const secret = decodeWorkspaceSecretV2(invite.secret)
    if (!secret) {
        throw new Error('Workspace invite contains an invalid workspace secret.')
    }

    const challenge = await fetchChallenge(
        invite.apiBaseUrl,
        secret.blobId,
        invite.mode === 'write' ? 'write' : 'read',
        invite.mode === 'write' ? 'writer' : 'reader',
        signal,
    )

    const readerProof = await createSubjectProof(
        base64UrlDecode(secret.readerSeed),
        secret.blobId,
        challenge.challengeId,
        'read',
        'reader',
    )

    const writerProof = invite.mode === 'write'
        ? await (() => {
            if (!secret.writerSeed) {
                throw new Error('Write invite is missing writerSeed in workspace secret.')
            }

            return createSubjectProof(
                base64UrlDecode(secret.writerSeed),
                secret.blobId,
                challenge.challengeId,
                'write',
                'writer',
            )
        })()
        : null

    const inviteRedeemUrl = `${invite.apiBaseUrl}/invites/redeem`
    const response = await fetch(inviteRedeemUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...getEphemeralHeaders() },
        body: JSON.stringify({
            inviteToken: invite.inviteToken,
            reader: readerProof,
            writer: writerProof,
            challengeId: challenge.challengeId,
        }),
        signal,
    })
    recordBrowserHttpErrorResponse(response, {
        url: `${invite.apiBaseUrl}/invites/redeem`,
        method: 'POST',
    })

    if (response.ok) {
        const payload = await response.json() as {
            blobId: string
            readToken?: string
            writeToken?: string
            expiresAt?: string
        }
        return {
            blobId: payload.blobId,
            readToken: payload.readToken,
            writeToken: payload.writeToken,
            expiresAt: payload.expiresAt,
            alreadyRedeemed: false,
            secret,
        }
    }

    let errorCode: string | undefined
    try {
        const payload = await response.json() as { error?: { code?: string } }
        errorCode = payload.error?.code
    } catch {
        errorCode = undefined
    }

    if (response.status === 409 && errorCode === 'INVITE_ALREADY_REDEEMED') {
        return {
            blobId: secret.blobId,
            alreadyRedeemed: true,
            secret,
        }
    }

    throw new Error(`Invite redeem failed: ${response.status}`)
}

/**
 * Create a workspace invite via the server control-plane (Spec §6.1 POST /invites, §13).
 *
 * This is the async replacement for the legacy sync `createShareLink()`.
 * It:
 * 1. Generates a fresh invite reader seed (+ writer seed for write mode).
 * 2. Derives invite subject identities (kid/pk).
 * 3. Calls POST /invites with the caller's write access token.
 * 4. Builds a WorkspaceInviteV2 envelope containing the invite token
 *    and a workspace-secret-v2 for the invitee (readKey + invite reader seed).
 *
 * The returned string is the base64url-encoded WorkspaceInviteV2,
 * suitable for `#invite=<result>` URLs or `sb-invite:<result>` clipboard format.
 */
export interface CreateWorkspaceInviteOptions {
    /** Base URL for the auth API, e.g. 'https://host/api/cryptoblobstore' */
    apiBaseUrl: string
    /** The inviter's workspace secret v2 (must contain writerSeed for write invites). */
    inviterSecret: WorkspaceSecretV2
    /** A valid write-scoped access token for the workspace. */
    writeToken: string
    /** Invite mode: 'read' grants read+subscribe, 'write' also grants commit+upload. */
    mode: 'read' | 'write'
    /** Optional UX metadata (Spec §4.2). */
    title?: string
    emoji?: string
    path?: string
    view?: string
    app?: string
}

export interface CreateWorkspaceInviteResult {
    /** The assembled WorkspaceInviteV2 object. */
    invite: WorkspaceInviteV2
    /** base64url-encoded WorkspaceInviteV2 — use in #invite= URL fragment. */
    encodedInvite: string
    /** The invitee's workspace secret v2 (contains readKey + invite readerSeed). */
    inviteeSecret: WorkspaceSecretV2
}

export async function createWorkspaceInviteV2(
    options: CreateWorkspaceInviteOptions,
    signal?: AbortSignal,
): Promise<CreateWorkspaceInviteResult> {
    const { apiBaseUrl, inviterSecret, writeToken, mode } = options

    // 1. Generate invitee seeds
    const inviteReaderSeedBytes = crypto.getRandomValues(new Uint8Array(32))
    const inviteReaderPk = await ed.getPublicKey(inviteReaderSeedBytes)
    const inviteReaderKid = await deriveKid(inviteReaderPk)

    let inviteWriterSeedBytes: Uint8Array | null = null
    let inviteWriterPk: Uint8Array | null = null
    let inviteWriterKid: string | null = null

    if (mode === 'write') {
        inviteWriterSeedBytes = crypto.getRandomValues(new Uint8Array(32))
        inviteWriterPk = await ed.getPublicKey(inviteWriterSeedBytes)
        inviteWriterKid = await deriveKid(inviteWriterPk)
    }

    // 2. Build invite subject identities for POST /invites request
    const readerIdentity = { kid: inviteReaderKid, pk: base64UrlEncode(inviteReaderPk) }
    const writerIdentity = inviteWriterKid && inviteWriterPk
        ? { kid: inviteWriterKid, pk: base64UrlEncode(inviteWriterPk) }
        : undefined

    // 3. Call POST /invites
    const inviteRequestPayload: Record<string, unknown> = {
        blobId: inviterSecret.blobId,
        mode,
        reader: readerIdentity,
        ...(writerIdentity ? { writer: writerIdentity } : {}),
        ...(options.title ? { title: options.title } : {}),
        ...(options.emoji ? { emoji: options.emoji } : {}),
        ...(options.path ? { path: options.path } : {}),
        ...(options.view ? { view: options.view } : {}),
        ...(options.app ? { app: options.app } : {}),
    }

    const inviteCreateUrl = `${apiBaseUrl}/invites`
    const response = await fetch(inviteCreateUrl, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            'authorization': `Bearer ${writeToken}`,
            ...getEphemeralHeaders(),
        },
        body: JSON.stringify(inviteRequestPayload),
        signal,
    })
    recordBrowserHttpErrorResponse(response, {
        url: `${apiBaseUrl}/invites`,
        method: 'POST',
    })

    if (!response.ok) {
        let detail = `Invite creation failed: ${response.status}`
        try {
            const body = await response.json() as { error?: { code?: string; message?: string } }
            if (body.error?.code) detail = `Invite creation failed: ${body.error.code}`
        } catch { /* use default message */ }
        throw new Error(detail)
    }

    const inviteResponse = await response.json() as { inviteToken: string; expiresAt: string }

    // 4. Build invitee's workspace secret v2 (readKey from inviter + invite seeds)
    const inviteeSecret: WorkspaceSecretV2 = {
        v: 2,
        type: 'workspace-secret',
        blobId: inviterSecret.blobId,
        readKey: inviterSecret.readKey,
        readerSeed: base64UrlEncode(inviteReaderSeedBytes),
        ...(inviteWriterSeedBytes ? { writerSeed: base64UrlEncode(inviteWriterSeedBytes) } : {}),
    }

    // 5. Build WorkspaceInviteV2 envelope (Spec §4.2)
    const invite: WorkspaceInviteV2 = {
        v: 2,
        type: 'workspace-invite',
        apiBaseUrl,
        mode,
        inviteToken: inviteResponse.inviteToken,
        secret: encodeWorkspaceSecretV2(inviteeSecret),
        ...(options.title ? { title: options.title } : {}),
        ...(options.emoji ? { emoji: options.emoji } : {}),
        ...(options.path ? { path: options.path } : {}),
        ...(options.view ? { view: options.view } : {}),
        ...(options.app ? { app: options.app } : {}),
    }

    const encodedInvite = encodeWorkspaceInviteV2(invite)

    return { invite, encodedInvite, inviteeSecret }
}

/**
 * Challenge-signing auth provider for the JS SDK transport layer.
 *
 * Implements WorkspaceAuthProvider by:
 * 1. Deriving reader/writer identities from seeds
 * 2. Fetching challenges from POST /auth/challenge
 * 3. Signing the auth-proof payload (Spec §4.4) with Ed25519
 * 4. Creating sessions via POST /auth/session
 * 5. Caching tokens with proactive refresh
 *
 * Usage:
 *   const secret = decodeWorkspaceSecretV2(encoded)
 *   const provider = new SessionTokenProvider(secret.blobId, apiBaseUrl, secret)
 *   const token = await provider.getAccessToken('read')
 *   // Use token.token as Authorization: Bearer value
 */
export class SessionTokenProvider implements WorkspaceAuthProvider {
    private readonly _blobId: string
    private readonly _apiBaseUrl: string
    private readonly _secret: WorkspaceSecretV2
    private readonly _readerSeed: Uint8Array
    private readonly _writerSeed: Uint8Array | null

    // Token cache (one per scope)
    private _cachedReadToken: WorkspaceAccessToken | null = null
    private _cachedWriteToken: WorkspaceAccessToken | null = null
    private _readAcquirePromise: Promise<WorkspaceAccessToken> | null = null
    private _writeAcquirePromise: Promise<WorkspaceAccessToken> | null = null

    // Lazy-derived identity
    private _readerKid: string | null = null
    private _readerPk: Uint8Array | null = null
    private _writerKid: string | null = null
    private _writerPk: Uint8Array | null = null
    private _identityPromise: Promise<void> | null = null
    private _bootstrapPromise: Promise<void> | null = null

    constructor(
        blobId: string,
        apiBaseUrl: string,
        secret: WorkspaceSecretV2,
    ) {
        this._blobId = blobId
        this._apiBaseUrl = apiBaseUrl
        this._secret = secret
        this._readerSeed = base64UrlDecode(secret.readerSeed)
        this._writerSeed = secret.writerSeed ? base64UrlDecode(secret.writerSeed) : null
    }

    get blobId(): string { return this._blobId }

    /** Derived reader kid (null until first getAccessToken call) */
    get readerKid(): string | null { return this._readerKid }

    /** Derived writer kid (null if read-only or until first call) */
    get writerKid(): string | null { return this._writerKid }

    async getAccessToken(scope: 'read' | 'write', signal?: AbortSignal): Promise<WorkspaceAccessToken> {
        if (scope === 'write' && this._writerSeed === null) {
            throw new Error('Cannot acquire write token: workspace secret has no writerSeed (read-only access)')
        }

        const cached = scope === 'write' ? this._cachedWriteToken : this._cachedReadToken
        if (cached && !this.tokenNeedsRefresh(cached)) return cached

        const inFlight = scope === 'write' ? this._writeAcquirePromise : this._readAcquirePromise
        if (inFlight) return inFlight

        const acquirePromise = this.acquireToken(scope, signal).finally(() => {
            if (scope === 'write') this._writeAcquirePromise = null
            else this._readAcquirePromise = null
        })

        if (scope === 'write') this._writeAcquirePromise = acquirePromise
        else this._readAcquirePromise = acquirePromise

        return acquirePromise
    }

    invalidateToken(scope: 'read' | 'write'): void {
        if (scope === 'write') {
            this._cachedWriteToken = null
            this._writeAcquirePromise = null
        } else {
            this._cachedReadToken = null
            this._readAcquirePromise = null
        }
    }

    // ── Private ──────────────────────────────────────────────────

    private tokenNeedsRefresh(token: WorkspaceAccessToken): boolean {
        const expiresAt = new Date(token.expiresAt).getTime()
        return Date.now() >= expiresAt - REFRESH_MARGIN_MS
    }

    private async ensureIdentity(): Promise<void> {
        if (!this._identityPromise) {
            this._identityPromise = (async () => {
                this._readerPk = await ed.getPublicKey(this._readerSeed)
                this._readerKid = await deriveKid(this._readerPk)

                if (this._writerSeed) {
                    this._writerPk = await ed.getPublicKey(this._writerSeed)
                    this._writerKid = await deriveKid(this._writerPk)
                }
            })()
        }
        return this._identityPromise
    }

    private async acquireToken(scope: 'read' | 'write', signal?: AbortSignal): Promise<WorkspaceAccessToken> {
        const useWriter = scope === 'write'
        const subjectKind = useWriter ? 'writer' as const : 'reader' as const

        await this.ensureIdentity()

        const kid = useWriter ? this._writerKid! : this._readerKid!
        const seed = useWriter ? this._writerSeed! : this._readerSeed

        let session: { accessToken: string; expiresAt: string }
        try {
            session = await this.fetchSessionToken(kid, seed, subjectKind, scope, signal)
        } catch (error) {
            if (!this.shouldBootstrapAfterAuthFailure(error)) throw error
            await this.ensureServerAclBootstrapped()
            session = await this.fetchSessionToken(kid, seed, subjectKind, scope, signal)
        }

        const token: WorkspaceAccessToken = {
            token: session.accessToken,
            expiresAt: session.expiresAt,
            blobId: this._blobId,
            scope,
        }

        // Step 4: Cache
        if (scope === 'write') this._cachedWriteToken = token
        else this._cachedReadToken = token

        return token
    }

    private shouldBootstrapAfterAuthFailure(error: unknown): boolean {
        if (!(error instanceof Error)) return false
        return error.message.includes('ACL_NOT_FOUND')
            || error.message === 'Auth session failed: 404'
            || error.message.includes('Auth session failed: 404 ')
    }

    private async ensureServerAclBootstrapped(): Promise<void> {
        if (!this._writerSeed) {
            throw new Error('Workspace ACL missing and secret cannot bootstrap server auth without writerSeed.')
        }

        if (!this._bootstrapPromise) {
            this._bootstrapPromise = (async () => {
                try {
                    await bootstrapWorkspace(this._apiBaseUrl, this._secret)
                } catch (error) {
                    if (error instanceof Error && error.message.includes('ACL_EXISTS')) return
                    throw error
                }
            })().finally(() => {
                this._bootstrapPromise = null
            })
        }

        await this._bootstrapPromise
    }

    private async fetchSessionToken(
        kid: string,
        seed: Uint8Array,
        subjectKind: 'reader' | 'writer',
        scope: 'read' | 'write',
        signal?: AbortSignal,
    ): Promise<{ accessToken: string; expiresAt: string }> {
        const challenge = await fetchChallenge(this._apiBaseUrl, this._blobId, scope, subjectKind, signal)
        const proof = await createSubjectProof(seed, this._blobId, challenge.challengeId, scope, subjectKind)
        return this.fetchSession(
            kid,
            proof.pk,
            proof.sig,
            subjectKind,
            challenge.challengeId,
            scope,
            signal,
        )
    }

    private async fetchSession(
        kid: string,
        pkB64: string,
        sigB64: string,
        subjectKind: 'reader' | 'writer',
        challengeId: string,
        scope: 'read' | 'write',
        signal?: AbortSignal,
    ): Promise<{ accessToken: string; expiresAt: string }> {
        const url = `${this._apiBaseUrl}/auth/session`
        const res = await fetchAuthResponseWithRetry(url, {
            method: 'POST',
            headers: { 'content-type': 'application/json', ...getEphemeralHeaders() },
            body: JSON.stringify({
                blobId: this._blobId,
                subject: { alg: 'ed25519', kind: subjectKind, kid, pk: pkB64, sig: sigB64 },
                challengeId,
                scope,
            }),
            signal,
        }, 'POST /auth/session', signal)
        if (!res.ok) {
            let code = ''
            try {
                const body = await res.json() as { error?: { code?: string } }
                code = body.error?.code ?? ''
            } catch {
                // Ignore parse failure — status alone is still useful.
            }
            throw new Error(code
                ? `Auth session failed: ${code} (${res.status})`
                : `Auth session failed: ${res.status}`)
        }
        return (await res.json()) as { accessToken: string; expiresAt: string }
    }
}

/**
 * Static token provider for tests. Returns a fixed token
 * without any challenge-signing flow.
 */
export class StaticTokenProvider implements WorkspaceAuthProvider {
    constructor(private readonly _token: WorkspaceAccessToken) {}

    get blobId(): string { return this._token.blobId }

    async getAccessToken(_scope: 'read' | 'write'): Promise<WorkspaceAccessToken> {
        return this._token
    }

    invalidateToken(_scope: 'read' | 'write'): void {
        // No-op for static provider
    }
}

// ── Bootstrap (Spec §6.1 POST /auth/bootstrap) ──────────────────

/**
 * Bootstrap a new workspace on the server: register ACL with reader + writer.
 * Returns initial workspace access tokens.
 *
 * This MUST be called before the first dataplane request for a brand-new blob
 * that has no server-side ACL yet (e.g. config-blob creation).
 */
export async function bootstrapWorkspace(
    apiBaseUrl: string,
    secret: WorkspaceSecretV2,
    signal?: AbortSignal,
): Promise<{ readToken: string; writeToken: string; expiresAt: string }> {
    // Step 1: Fetch challenge (scope=write, subjectKind=writer for bootstrap)
    const challenge = await fetchChallenge(
        apiBaseUrl,
        secret.blobId,
        'write',
        'writer',
        signal,
    )

    if (!secret.writerSeed) {
        throw new Error('Cannot bootstrap workspace: writerSeed is required')
    }

    const writerSeed = base64UrlDecode(secret.writerSeed)
    const writerProof = await createSubjectProof(
        writerSeed,
        secret.blobId,
        challenge.challengeId,
        'write',
        'writer',
    )

    // Step 2: Reader proves possession of the initial reader key, but for bootstrap
    // both signatures must cover the same writer-scoped payload (server contract).
    const readerSeed = base64UrlDecode(secret.readerSeed)
    const readerProof = await createSubjectProof(
        readerSeed,
        secret.blobId,
        challenge.challengeId,
        'write',
        'writer',
        writerProof.kid,
    )

    // Step 3: POST /auth/bootstrap
    const bootstrapUrl = `${apiBaseUrl}/auth/bootstrap`
    const res = await fetchAuthResponseWithRetry(bootstrapUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...getEphemeralHeaders() },
        body: JSON.stringify({
            blobId: secret.blobId,
            reader: { alg: readerProof.alg, kid: readerProof.kid, pk: readerProof.pk, sig: readerProof.sig },
            writer: { alg: writerProof.alg, kid: writerProof.kid, pk: writerProof.pk, sig: writerProof.sig },
            challengeId: challenge.challengeId,
        }),
        signal,
    }, 'POST /auth/bootstrap', signal)

    if (!res.ok) {
        const body = await res.text().catch(() => '')
        throw new Error(`Auth bootstrap failed: ${res.status} ${body}`)
    }

    return (await res.json()) as { readToken: string; writeToken: string; expiresAt: string }
}
